import { ASSIGNED_SEATING } from "@shared/constants";
import { useTranslation } from "@shared/utils/internationalization";
import { SeatingItemPropertyMap } from "@shared/modules/seating/types/seating-property-types";
import {
  MapPermissionsType,
  ItemSelectReturnType,
  SeatWithLocationDataType,
} from "@shared/modules/seating/types/seating-types";
import { isTicketTypeWaitlisted } from "@shared/modules/ticket-types/utils/ticket-type-utils";
import { useCallback, useEffect, useState } from "react";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { ItemSelectModalProps, MapItemType } from "./ItemSelectModal.props";

export function useItemSelectModal(props: ItemSelectModalProps) {
  const { t } = useTranslation("seating");
  const {
    seatingServiceState,
    getInfoRequiredLocationQtyInCart,
    getLocationQtyInCart,
  } = useSeatingState();
  const { itemsOnSeat, resolve, ticketAuthArray, isDistributeGroupSale } =
    props;
  const { mapItemType } = seatingServiceState;
  const { itemGroups, areAllRenewalItemGroups } = useBasketService();

  const [locationSelections, setLocationSelections] = useState<
    Array<ItemSelectReturnType>
  >([]);

  const [itemSelectData, setItemSelectData] = useState<{
    title: string;
    itemTypes: Array<MapItemType>;
    type: string;
  }>();

  const { $data, $name, $permissions } = itemsOnSeat;

  // if the cart permission exists, return the basket quantity
  // else return 0
  const getCurrentQtyInBasket = useCallback(
    (itemId: number, permissionId: number): number => {
      if (seatingServiceState.getCartPermission(permissionId)) {
        if (isDistributeGroupSale) {
          return Number(
            seatingServiceState?.getCartPermission(permissionId)?.location
              ?.count ?? 0
          );
        }
        // info_required items with locations are split into different item_groups
        // the quantity will be the number of item_groups with the same id value
        const itemGroup = itemGroups?.find(
          (ig) => ig.type === itemId || ig.membership_level === itemId
        );
        if (itemGroup?.info_required) {
          return getInfoRequiredLocationQtyInCart(itemId, permissionId, 0);
        }

        return getLocationQtyInCart(itemId, permissionId, 0);
      }
      return 0;
    },
    [itemGroups, seatingServiceState, mapItemType]
  );

  // get the current selected amounts for each item type
  // either from the basket or from the locationSelections array
  const getCurrentSelectedAmount = useCallback(
    (
      item: SeatingItemPropertyMap & {
        permissionId: number;
      }
    ): number => {
      if (
        !locationSelections.length ||
        !locationSelections.find((loc) => loc.itemTypeId === item.id)
      ) {
        return getCurrentQtyInBasket(item.id, item.permissionId) ?? 0;
      }
      return (
        locationSelections.find((loc) => loc.itemTypeId === item.id)
          ?.requestAmount ?? 0
      );
    },
    [locationSelections, getCurrentQtyInBasket]
  );

  const selectItemPermission = useCallback(
    ({
      itemTypeId,
      permissionId,
      minPurchaseLimit,
      requestAmount,
    }: {
      itemTypeId: number;
      permissionId: number;
      minPurchaseLimit?: number;
      requestAmount?: number;
    }) => {
      if (itemsOnSeat && $name === ASSIGNED_SEATING.SEAT.ID) {
        // close the modal and return data
        resolve({
          itemTypeId,
          permissionId,
          requestAmount,
          ticketAuthArray,
          minPurchaseLimit,
        });
      } else if (
        itemsOnSeat &&
        $name === ASSIGNED_SEATING.GENERAL_ADMISSION.ID
      ) {
        const returnData = {
          itemTypeId,
          permissionId,
          requestAmount,
          ticketAuthArray,
          minPurchaseLimit,
        };

        const newLocationSelections = locationSelections
          .filter((selection) => selection.itemTypeId !== returnData.itemTypeId)
          .concat([returnData]);
        setLocationSelections(newLocationSelections);
      }
    },
    [resolve, ticketAuthArray, itemsOnSeat, $name, locationSelections]
  );

  // get the correct modal title, item types, and modal type for seats
  const handleSeatData = useCallback(() => {
    if (!$permissions.length) return;
    const title = `${t("Section")} ${
      ($data as SeatWithLocationDataType).segment_identifier
    }, ${t("Row")} ${
      ($data as SeatWithLocationDataType).location_identifier
    }, ${t("Seat")} ${$data?.identifier}`;

    // default permissions array is $permissions
    let permissionsArray = $permissions;

    // if there are seats in the pending cart,
    // get the item type in the pending cart and only show that item type on the selection modal
    if (
      seatingServiceState.pendingCartPermissions.length > 0 &&
      !areAllRenewalItemGroups
    ) {
      permissionsArray = $permissions.filter(
        (p) =>
          p.seatingItemTypeId ===
          seatingServiceState.pendingCartPermissions[0].itemTypeId
      );
    }
    const itemTypes: Array<MapItemType> = permissionsArray.map((p) => {
      const itemType: SeatingItemPropertyMap =
        seatingServiceState.itemTypeMap[
          p.seatingItemTypeId as keyof typeof seatingServiceState.itemTypeMap
        ];
      return {
        ...itemType,
        permissionId: Number(p.seatingItemPermissionId),
      };
    });

    setItemSelectData({
      title,
      itemTypes,
      type: $name,
    });
  }, [$data, $name, $permissions, seatingServiceState, t]);

  // get the correct modal title, item types, and modal type for locations
  const handleLocationData = useCallback(() => {
    const title = `${t("Location")} ${$data?.identifier}`;

    const itemTypes: Array<MapItemType> = $permissions.map(
      (p: MapPermissionsType) => {
        const cartLocationPermission = seatingServiceState.getCartPermission(
          Number(p.seatingItemPermissionId)
        );
        const itemType: SeatingItemPropertyMap =
          seatingServiceState?.itemTypeMap[
            p.seatingItemTypeId as keyof typeof seatingServiceState.itemTypeMap
          ];
        const requestAmount =
          (cartLocationPermission && cartLocationPermission?.location?.count) ||
          0;

        // If this item type is waitlisted, ignore the usages remaining
        const usageRemaining = !isTicketTypeWaitlisted(itemType)
          ? Number(p?.usage_remaining) + requestAmount
          : Number(itemType?.$countOptions?.length) - 1;

        const $countOptions =
          itemType?.$countOptions?.filter(
            (option: number) => option <= usageRemaining
          ) ?? [];

        return {
          ...itemType,
          $countOptions,
          permissionId: Number(p.seatingItemPermissionId),
          requestAmount,
          usageRemaining,
        };
      }
    );

    setItemSelectData({
      title,
      itemTypes,
      type: $name,
    });
  }, [$data, $name, $permissions, seatingServiceState, t]);

  useEffect(() => {
    // if modal data doesn't exist, get the data for the particular element type
    if (!itemSelectData) {
      // get seat data
      if ($name === ASSIGNED_SEATING.SEAT.ID) {
        handleSeatData();
      }
      // get location data
      if ($name === ASSIGNED_SEATING.GENERAL_ADMISSION.ID) {
        handleLocationData();
      }
    }
  }, [itemSelectData, handleLocationData, handleSeatData, $name, $permissions]);

  return {
    locationSelections,
    itemSelectData,
    selectItemPermission,
    getCurrentSelectedAmount,
  };
}
